import classNames from 'classnames'
import type { ElementType, ReactNode } from 'react'

export type TextVariant =
  | 'display-xl' | 'display-lg' | 'display-md'
  | 'heading-lg' | 'heading-md' | 'heading-sm'
  | 'body-lg'    | 'body-md'    | 'body-sm'
  | 'meta-md'    | 'meta-sm'

export type TextTone =
  | 'primary' | 'secondary' | 'muted' | 'accent' | 'danger' | 'inverted'

export interface TextProps {
  variant?: TextVariant
  tone?: TextTone
  as?: ElementType
  truncate?: boolean
  numberOfLines?: number
  className?: string
  children: ReactNode
}

const variantClasses: Record<TextVariant, string> = {
  'display-xl': 'text-[40px] leading-[48px] font-heading font-bold',
  'display-lg': 'text-[32px] leading-[40px] font-heading font-bold',
  'display-md': 'text-[28px] leading-[36px] font-heading font-bold',
  'heading-lg': 'text-[24px] leading-[32px] font-heading font-semibold',
  'heading-md': 'text-[20px] leading-[28px] font-heading font-semibold',
  'heading-sm': 'text-[18px] leading-[26px] font-heading font-medium',
  'body-lg':    'text-[17px] leading-[26px] font-body',
  'body-md':    'text-[15px] leading-[24px] font-body',
  'body-sm':    'text-[13px] leading-[20px] font-body',
  'meta-md':    'text-[12px] leading-[16px] font-body',
  'meta-sm':    'text-[11px] leading-[14px] font-body',
}

const toneClasses: Record<TextTone, string> = {
  primary:   'text-text-main',
  secondary: 'text-primary',
  muted:     'text-text-muted',
  accent:    'text-secondary',
  danger:    'text-red-400',
  inverted:  'text-background',
}

export function Text({
  variant = 'body-md',
  tone = 'primary',
  as: Tag = 'p',
  truncate,
  numberOfLines,
  className,
  children,
}: TextProps) {
  const style = numberOfLines
    ? { WebkitLineClamp: numberOfLines, display: '-webkit-box', WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }
    : undefined

  return (
    <Tag
      className={classNames(
        variantClasses[variant],
        toneClasses[tone],
        truncate && 'truncate',
        numberOfLines && 'overflow-hidden',
        className,
      )}
      style={style}
    >
      {children}
    </Tag>
  )
}
